import type { RunLogChunk } from '@/types/api';

/**
 * Turning what a server printed into lines a person can read.
 *
 * ## What the raw output contains
 *
 * Package managers draw progress by writing a carriage return and redrawing the same line, and some
 * tools colour their output with ANSI escape codes. Neither means anything in a browser: shown as
 * they are, a progress bar becomes a thousand copies of itself and a coloured word becomes
 * `\x1b[32mdone\x1b[0m`. So escape codes are removed and only what a terminal would have left on each
 * line is kept.
 *
 * ## Markers
 *
 * Playbooks announce what they found and did with `IAP-OK`, `IAP-CHANGED` and the rest. The log keeps
 * those lines, and marks them, because they are the part of a long install worth reading.
 */

export type MarkerKind = 'ok' | 'changed' | 'would' | 'note' | 'error' | 'result';

export interface LogLine {
  /** Stable within one log: its position. */
  id: number;
  stream: 'out' | 'sys';
  text: string;
  marker: MarkerKind | null;
}

const ESC = String.fromCharCode(27);
const BEL = String.fromCharCode(7);
// Built from character codes so the pattern holds no literal control characters.
const ANSI = new RegExp(`${ESC}\\[[0-9;?]*[ -/]*[@-~]|${ESC}\\][^${BEL}]*(?:${BEL}|${ESC}\\\\)`, 'g');
const MARKER = /^IAP-(OK|CHANGED|WOULD|NOTE|ERROR|RESULT) (.*)$/;

function clean(line: string): string {
  const withoutEscapes = line.replace(ANSI, '');
  const lastReturn = withoutEscapes.lastIndexOf('\r');

  return lastReturn === -1 ? withoutEscapes : withoutEscapes.slice(lastReturn + 1);
}

/**
 * Accumulates chunks into lines. Output chunks can end in the middle of a line, so the unfinished
 * tail is carried over until the rest of it arrives.
 */
export class LogBuffer {
  private lines: LogLine[] = [];
  private partial: { stream: 'out' | 'sys'; text: string } | null = null;
  private nextId = 0;

  append(chunks: RunLogChunk[]): void {
    for (const chunk of chunks) {
      if (chunk.stream === 'sys') {
        this.flushPartial();
        for (const line of chunk.content.replace(/\n$/, '').split('\n')) {
          this.push('sys', line);
        }
        continue;
      }

      const text = (this.partial?.stream === 'out' ? this.partial.text : '') + chunk.content;
      if (this.partial?.stream !== 'out') {
        this.flushPartial();
      }

      const pieces = text.split('\n');
      const tail = pieces.pop() ?? '';

      for (const piece of pieces) {
        this.push('out', piece);
      }

      this.partial = tail === '' ? null : { stream: 'out', text: tail };
    }
  }

  /** Every complete line, and the unfinished one at the end, as it stands now. */
  view(): LogLine[] {
    if (this.partial === null) {
      return this.lines;
    }

    return [...this.lines, this.toLine(this.partial.stream, this.partial.text, this.nextId)];
  }

  private flushPartial(): void {
    if (this.partial !== null) {
      this.push(this.partial.stream, this.partial.text);
      this.partial = null;
    }
  }

  private push(stream: 'out' | 'sys', raw: string): void {
    this.lines.push(this.toLine(stream, raw, this.nextId));
    this.nextId += 1;
  }

  private toLine(stream: 'out' | 'sys', raw: string, id: number): LogLine {
    const text = clean(raw);
    const match = stream === 'out' ? MARKER.exec(text) : null;

    return {
      id,
      stream,
      text: match?.[2] ?? text,
      marker: match ? (match[1]?.toLowerCase() as MarkerKind) : null,
    };
  }
}
